import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { getData, postData } from "../../../FetchApiServices";
import { useToast } from "../Layout/T2TUIProvider";
import { useGuidance } from "./T2TGuidanceProvider";

// ---- Fallback system templates (used if API returns nothing) ----
const SYSTEM_TEMPLATES = [
  // Vendor Master templates (T2T-03)
  {
    id: "vendor-domestic-4stage", name: "Vendor Onboarding — Standard Domestic (4-Stage)", category: "vendor_master",
    stage_count: 5, difficulty: "intermediate", estimated_minutes: 15, is_featured: true,
    description: "Full Krypton-flow domestic vendor onboarding: Initiator fills general data, Checker verifies, Finance enters tax/bank with verification badges, MDM Approver gives final sign-off, then SAP push creates the vendor.",
    use_case: "You need to onboard a regular domestic vendor (Z101/Z104) with PAN, GST, and bank verification before SAP creation.",
    stages_preview: [
      { name: "Initiator", role: "Vendor Initiator", desc: "Fills General Data, Address, Purchasing info" },
      { name: "Checker", role: "Vendor Checker", desc: "Verifies all data. Approve or Reject" },
      { name: "Finance", role: "Finance Data Controller", desc: "Enters Tax (GST), Bank details. Sees verification badges" },
      { name: "MDM Approver", role: "MDM Approver", desc: "Final review + dedup check. Approve \u2192 SAP Push" },
      { name: "End", role: "System", desc: "BAPI_VENDOR_CREATE \u2192 Vendor created in SAP + OSIF" },
    ],
    account_groups: ["Z101", "Z104"],
    verifications: ["PAN", "GST", "Bank Account"],
  },
  {
    id: "vendor-domestic-3stage", name: "Vendor Onboarding — Simplified Domestic (3-Stage)", category: "vendor_master",
    stage_count: 4, difficulty: "beginner", estimated_minutes: 10,
    description: "Simplified domestic onboarding where the Initiator fills the complete form including tax and bank. Checker verifies, Approver signs off, SAP push fires.",
    use_case: "Mid-size vendors (Z102) where Finance step is handled by Initiator directly.",
    stages_preview: [
      { name: "Initiator", role: "Vendor Initiator", desc: "Fills complete form including Tax and Bank" },
      { name: "Checker", role: "Vendor Checker", desc: "Verifies full data + verification badges" },
      { name: "Approver", role: "MDM Approver", desc: "Final approval \u2192 SAP Push" },
      { name: "End", role: "System", desc: "BAPI_VENDOR_CREATE" },
    ],
    account_groups: ["Z102"],
    verifications: ["PAN", "GST", "Bank Account"],
  },
  {
    id: "vendor-domestic-fasttrack", name: "Vendor — Fast Track (Seasonal / One-Time)", category: "vendor_master",
    stage_count: 3, difficulty: "beginner", estimated_minutes: 5,
    description: "Quick 2-stage flow for seasonal/kaccha vendors and one-time purchases. Initiator fills basic info, Approver signs off directly.",
    use_case: "Seasonal agricultural suppliers (Z103) or one-time purchases below threshold (Z105). PAN only \u2014 GST optional.",
    stages_preview: [
      { name: "Initiator", role: "Vendor Initiator", desc: "Fills basic info. PAN mandatory, GST optional" },
      { name: "Approver", role: "MDM Approver", desc: "Approves directly \u2192 SAP Push" },
      { name: "End", role: "System", desc: "BAPI_VENDOR_CREATE with minimal fields" },
    ],
    account_groups: ["Z103", "Z105"],
    verifications: ["PAN"],
  },
  {
    id: "vendor-intl-4stage", name: "International Vendor (4-Stage)", category: "vendor_master",
    stage_count: 5, difficulty: "intermediate", estimated_minutes: 15,
    description: "Full international vendor onboarding with country-specific tax fields. Initiator fills company info, Checker verifies, Finance enters VAT/SWIFT/IBAN, MDM Approver signs off.",
    use_case: "Foreign manufacturers, importers, global service providers (Z201) requiring VAT + SWIFT verification.",
    stages_preview: [
      { name: "Initiator", role: "Vendor Initiator", desc: "Fills company info, country, address, category" },
      { name: "Checker", role: "Vendor Checker", desc: "Verifies submitted data" },
      { name: "Finance", role: "Finance Data Controller", desc: "Enters VAT, Tax ID, SWIFT/IBAN. Verification badges shown" },
      { name: "Approver", role: "MDM Approver", desc: "Final review + dedup \u2192 SAP Push" },
      { name: "End", role: "System", desc: "BAPI_VENDOR_CREATE with international mapping" },
    ],
    account_groups: ["Z201"],
    verifications: ["VAT", "SWIFT/IBAN"],
  },
  {
    id: "vendor-intl-3stage", name: "International Vendor — Simplified (3-Stage)", category: "vendor_master",
    stage_count: 4, difficulty: "beginner", estimated_minutes: 10,
    description: "Simplified international vendor flow. Initiator fills complete form including Tax ID and SWIFT. Checker verifies, Approver signs off.",
    use_case: "Low-value international vendors or single-region suppliers (Z202).",
    stages_preview: [
      { name: "Initiator", role: "Vendor Initiator", desc: "Fills complete international form including Tax and SWIFT" },
      { name: "Checker", role: "Vendor Checker", desc: "Verifies all data + SWIFT verification badge" },
      { name: "Approver", role: "MDM Approver", desc: "Final approval \u2192 SAP Push" },
      { name: "End", role: "System", desc: "BAPI_VENDOR_CREATE" },
    ],
    account_groups: ["Z202"],
    verifications: ["Tax Number", "SWIFT"],
  },

  // General workflow templates
  { id: "purchase-order",         name: "Purchase Order Approval",       category: "procurement", stage_count: 3, difficulty: "beginner",  estimated_minutes: 5,  description: "Purchase requests routed through manager and finance approval.", use_case: "Any purchase above a set amount needs manager and finance approval." },
  { id: "expense-claim",          name: "Expense Claim",                 category: "finance",     stage_count: 3, difficulty: "beginner",  estimated_minutes: 5,  description: "Employee expense reimbursement with line manager and finance review.", use_case: "Employees need to claim reimbursement for work expenses." },
  { id: "capex-budget",           name: "Capex / Budget Request",        category: "finance",     stage_count: 4, difficulty: "intermediate", estimated_minutes: 10, description: "Capital expenditure or budget increase requiring multi-level sign-off.", use_case: "Capital expenditure or budget increase requires HOD, Finance, and CFO approval." },
  { id: "employee-onboarding",    name: "Employee Onboarding",           category: "hr",          stage_count: 4, difficulty: "intermediate", estimated_minutes: 10, description: "New joiner clearance across HR, Finance, IT, and Access teams.", use_case: "New joiner needs documents collected and multi-department clearance." },
  { id: "contract-approval",      name: "Contract Approval",             category: "legal",       stage_count: 3, difficulty: "intermediate", estimated_minutes: 8,  description: "Contract review and management approval before signing.", use_case: "Any contract needs legal review and management approval before signing." },
  { id: "leave-request",          name: "Leave / Time-Off Request",      category: "hr",          stage_count: 2, difficulty: "beginner",  estimated_minutes: 3,  description: "Simple employee leave requests needing line manager approval.", use_case: "Simple employee leave requests needing line manager approval." },
  { id: "custom-blank",           name: "Custom Blank Workflow",         category: "general",     stage_count: 0, difficulty: "advanced",  estimated_minutes: 15, description: "Start from a blank canvas and build your own process from scratch.", use_case: "You have a unique process that none of the templates match." },
];

const CATEGORIES = [
  { id: "all",           label: "All" },
  { id: "vendor_master", label: "Vendor Master" },
  { id: "procurement",   label: "Procurement" },
  { id: "finance",       label: "Finance" },
  { id: "hr",            label: "HR" },
  { id: "legal",         label: "Legal" },
  { id: "general",       label: "General" },
];

const DIFFICULTY_COLORS = {
  beginner:     { bg: "#F0FDF4", color: "#166534", border: "#BBF7D0" },
  intermediate: { bg: "#FFF7ED", color: "#9A3412", border: "#FED7AA" },
  advanced:     { bg: "#FEF2F2", color: "#991B1B", border: "#FECACA" },
};

const T2TTemplateGallery = ({ open, onClose, onSelectTemplate, onStartBlank }) => {
  const toast = useToast();
  const { logEvent } = useGuidance();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");
  const [detailTemplate, setDetailTemplate] = useState(null);
  const [creating, setCreating] = useState(false);

  // Fetch templates from API or use fallbacks
  useEffect(() => {
    if (!open) return;
    const fetchTemplates = async () => {
      setLoading(true);
      const resp = await getData("t2t/workflow-templates");
      if (!resp.error) {
        const raw = resp.data?.data;
        const list = Array.isArray(raw) ? raw : (raw?.templates || raw?.rows || []);
        setTemplates(list.length > 0 ? list : SYSTEM_TEMPLATES);
      } else {
        setTemplates(SYSTEM_TEMPLATES);
      }
      setLoading(false);
    };
    fetchTemplates();
  }, [open]);

  if (!open) return null;

  const filtered = activeCategory === "all" ? templates : templates.filter((t) => t.category === activeCategory);

  const handleUseTemplate = async (template) => {
    if (template.id === "custom-blank" || template.stage_count === 0) {
      logEvent("template_selected", { template_id: template.id, template_name: template.name, action: "blank" });
      onClose();
      if (onStartBlank) onStartBlank();
      return;
    }

    setCreating(true);
    const resp = await postData(`t2t/workflow-templates/${template.id}/use`, {});
    if (resp.error) {
      toast(resp.error.message || "Failed to create workflow from template.", "error");
      setCreating(false);
      return;
    }
    const newWfId = resp.data?.data?.workflow_id;
    logEvent("template_selected", { template_id: template.id, template_name: template.name, workflow_id: newWfId });
    toast(`Workflow created from "${template.name}"!`, "success");
    setCreating(false);
    onClose();
    if (onSelectTemplate) onSelectTemplate(newWfId);
  };

  return (
    <div className="t2t-welcome-overlay" onClick={onClose}>
      <div className="t2t-template-gallery" onClick={(e) => e.stopPropagation()}>

        {/* Detail View */}
        {detailTemplate ? (
          <div className="t2t-template-detail">
            <div className="t2t-template-detail-header">
              <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={() => setDetailTemplate(null)}>← Back to gallery</button>
              <button className="t2t-close-btn" onClick={onClose}>×</button>
            </div>
            <div style={{ padding: "0 24px 24px" }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 6px" }}>{detailTemplate.name}</h3>
              <div style={{ display: "flex", gap: 12, marginBottom: 16, fontSize: 12, color: "var(--t2t-text-muted)" }}>
                <span>⏱ {detailTemplate.estimated_minutes} min to customise</span>
                <span>{detailTemplate.stage_count} stages</span>
                <span style={{ ...DIFFICULTY_COLORS[detailTemplate.difficulty], padding: "1px 8px", borderRadius: 20, fontSize: 10, fontWeight: 700, border: `1px solid ${DIFFICULTY_COLORS[detailTemplate.difficulty]?.border}` }}>
                  {detailTemplate.difficulty}
                </span>
              </div>

              {/* Flow preview — rich stage table if stages_preview exists, else generic */}
              {detailTemplate.stages_preview ? (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Workflow Stages:</div>
                  <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#F8FAFC" }}>
                        <th style={{ padding: "6px 10px", textAlign: "left", fontWeight: 700, borderBottom: "1px solid #E2E8F0" }}>Stage</th>
                        <th style={{ padding: "6px 10px", textAlign: "left", fontWeight: 700, borderBottom: "1px solid #E2E8F0" }}>Role</th>
                        <th style={{ padding: "6px 10px", textAlign: "left", fontWeight: 700, borderBottom: "1px solid #E2E8F0" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailTemplate.stages_preview.map((s, i) => (
                        <tr key={i} style={{ borderBottom: "1px solid #F1F5F9", background: s.name === "End" ? "#1B3A6B" : "white", color: s.name === "End" ? "white" : "inherit" }}>
                          <td style={{ padding: "6px 10px", fontWeight: 600 }}>{s.name}</td>
                          <td style={{ padding: "6px 10px", fontSize: 10, color: s.name === "End" ? "rgba(255,255,255,0.7)" : "var(--t2t-text-muted)" }}>{s.role}</td>
                          <td style={{ padding: "6px 10px", fontSize: 10, color: s.name === "End" ? "rgba(255,255,255,0.7)" : "var(--t2t-text-secondary)" }}>{s.desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : detailTemplate.stage_count > 0 && (
                <div style={{ background: "#F8FAFC", borderRadius: 8, padding: 16, marginBottom: 16, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
                  {Array.from({ length: detailTemplate.stage_count }, (_, i) => (
                    <React.Fragment key={i}>
                      <div style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 6, padding: "6px 12px", fontSize: 11, fontWeight: 600 }}>
                        Stage {i + 1}
                      </div>
                      {i < detailTemplate.stage_count - 1 && <span style={{ color: "#94A3B8" }}>{"\u2192"}</span>}
                    </React.Fragment>
                  ))}
                  <span style={{ color: "#94A3B8" }}>{"\u2192"}</span>
                  <div style={{ background: "#1B3A6B", color: "white", borderRadius: 6, padding: "6px 12px", fontSize: 11, fontWeight: 600 }}>End</div>
                </div>
              )}

              {/* Account groups & verifications — vendor templates only */}
              {detailTemplate.account_groups && (
                <div style={{ display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--t2t-text-muted)", marginBottom: 4 }}>Account Groups</div>
                    <div style={{ display: "flex", gap: 4 }}>
                      {detailTemplate.account_groups.map((ag) => (
                        <span key={ag} style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: "#EFF6FF", color: "#1D4ED8", border: "1px solid #BFDBFE" }}>{ag}</span>
                      ))}
                    </div>
                  </div>
                  {detailTemplate.verifications && (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--t2t-text-muted)", marginBottom: 4 }}>Verifications Required</div>
                      <div style={{ display: "flex", gap: 4 }}>
                        {detailTemplate.verifications.map((v) => (
                          <span key={v} style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 4, background: "#F0FDF4", color: "#166534", border: "1px solid #BBF7D0" }}>{v}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>What this workflow does:</div>
                <p style={{ fontSize: 13, color: "var(--t2t-text-secondary)", lineHeight: 1.6, margin: 0 }}>{detailTemplate.description}</p>
              </div>

              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Use this when:</div>
                <p style={{ fontSize: 13, color: "var(--t2t-text-secondary)", lineHeight: 1.6, margin: 0 }}>{detailTemplate.use_case}</p>
              </div>

              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>You will need to:</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ fontSize: 12, color: "var(--t2t-text-secondary)" }}>☐ Set who can play each role (assign your team members)</div>
                  <div style={{ fontSize: 12, color: "var(--t2t-text-secondary)" }}>☐ Choose what information to collect (form fields)</div>
                  <div style={{ fontSize: 12, color: "var(--t2t-text-secondary)" }}>☐ Set SLA deadlines (optional)</div>
                </div>
              </div>

              <div style={{ display: "flex", gap: 12 }}>
                <button className="t2t-btn t2t-btn-primary" onClick={() => handleUseTemplate(detailTemplate)} disabled={creating}>
                  {creating ? "Creating..." : "Use this template"}
                </button>
                <button className="t2t-btn t2t-btn-ghost" onClick={() => setDetailTemplate(null)}>Choose a different one</button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Gallery Header */}
            <div className="t2t-template-gallery-header">
              <div>
                <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Template Gallery</h3>
                <p style={{ fontSize: 13, color: "var(--t2t-text-muted)", margin: "4px 0 0" }}>Pick a starting point and customise it for your team</p>
              </div>
              <button className="t2t-close-btn" onClick={onClose}>×</button>
            </div>

            {/* Category Tabs */}
            <div style={{ padding: "0 24px", display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 16 }}>
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  style={{
                    padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600, border: "1px solid",
                    cursor: "pointer", transition: "0.15s",
                    background: activeCategory === cat.id ? "var(--t2t-primary)" : "white",
                    color: activeCategory === cat.id ? "white" : "var(--t2t-text-secondary)",
                    borderColor: activeCategory === cat.id ? "var(--t2t-primary)" : "#E2E8F0",
                  }}
                >{cat.label}</button>
              ))}
            </div>

            {/* Template Cards */}
            <div style={{ padding: "0 24px 24px", overflowY: "auto", flex: 1 }}>
              {loading ? (
                <div style={{ textAlign: "center", padding: 40, color: "var(--t2t-text-muted)", fontSize: 13 }}>Loading templates...</div>
              ) : filtered.length === 0 ? (
                <div style={{ textAlign: "center", padding: 40, color: "var(--t2t-text-muted)", fontSize: 13 }}>No templates in this category.</div>
              ) : (
                <div className="t2t-template-grid">
                  {filtered.map((t) => {
                    const dc = DIFFICULTY_COLORS[t.difficulty] || DIFFICULTY_COLORS.beginner;
                    return (
                      <div key={t.id} className="t2t-template-card" onClick={() => t.id === "custom-blank" ? handleUseTemplate(t) : setDetailTemplate(t)}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 8px", borderRadius: 20, background: dc.bg, color: dc.color, border: `1px solid ${dc.border}`, textTransform: "capitalize" }}>
                            {t.difficulty}
                          </span>
                          <span style={{ fontSize: 10, color: "var(--t2t-text-muted)" }}>
                            {t.stage_count > 0 ? `${t.stage_count} stages` : "Blank"} · ⏱ {t.estimated_minutes}m
                          </span>
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{t.name}</div>
                        <div style={{ fontSize: 11, color: "var(--t2t-text-secondary)", lineHeight: 1.5, marginBottom: 10 }}>{t.description}</div>
                        <div style={{ fontSize: 11, color: "var(--t2t-primary)", fontWeight: 600 }}>
                          {t.id === "custom-blank" ? "Open blank canvas →" : "View details →"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

T2TTemplateGallery.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelectTemplate: PropTypes.func,
  onStartBlank: PropTypes.func,
};

export default T2TTemplateGallery;
